<template>
  <div class="con-tab-ejemplo">
    <!-- CARGANDO -->
    <div v-if="uiState === 'loading'" class="gw-sla-centro">
      <div class="gw-spinner" aria-label="Cargando"></div>
      <div class="gw-sla-centro-titulo">Cargando los tiempos…</div>
    </div>

    <!-- ERROR -->
    <div v-else-if="uiState === 'error'" class="gw-sla-centro">
      <div class="gw-sla-centro-titulo">No fue posible cargar los tiempos</div>
      <div class="gw-sla-centro-texto">
        Vuelve a intentarlo en unos minutos o selecciona otro rango de fechas.
      </div>
    </div>

    <!-- VACÍO: dos motivos distintos, y confundirlos desorienta -->
    <sla-empty-state
      v-else-if="uiState === 'vacio'"
      :motivo="motivoVacio"
      :medicion-desde="medicionDesde"
      :desde="startDate"
      :hasta="endDate"
    />

    <!-- CON DATOS -->
    <template v-else>
      <div v-if="data.advertencia" class="gw-advertencia">
        {{ data.advertencia }}
      </div>

      <div class="vx-row">
        <div class="vx-col w-full mb-base">
          <sla-compliance-kpi
            :resumen="data.procesos"
            :tendencia="data.tendencia_semanal"
            :meta="data.meta_de_cartera"
          />
        </div>
      </div>

      <div class="vx-row">
        <div class="vx-col w-full mb-base">
          <sla-time-by-owner :reparto="data.tiempo_por_responsable" />
        </div>
      </div>

      <div class="vx-row">
        <div class="vx-col w-full mb-base">
          <sla-time-by-phase :fases="data.tiempo_por_fase" />
        </div>
      </div>

      <div class="vx-row">
        <div class="vx-col w-full mb-base">
          <sla-top-reasons :motivos="data.motivos_de_demora" />
        </div>
      </div>

      <div class="vx-row">
        <div class="vx-col w-full mb-base">
          <sla-distribution-table
            :distribucion="data.distribucion_por_validacion"
            :meta="data.meta_de_cartera"
          />
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { eventBus } from "./../../main";
import api from "../../mixins/api";
import slaFormat from "../../mixins/slaFormat";

import SlaComplianceKpi from "./SlaComplianceKpi.vue";
import SlaTimeByOwner from "./SlaTimeByOwner.vue";
import SlaTimeByPhase from "./SlaTimeByPhase.vue";
import SlaTopReasons from "./SlaTopReasons.vue";
import SlaDistributionTable from "./SlaDistributionTable.vue";
import SlaEmptyState from "./SlaEmptyState.vue";

export default {
  name: "SlaTab",
  mixins: [api, slaFormat],
  props: ["startDate", "endDate"],

  components: {
    "sla-compliance-kpi": SlaComplianceKpi,
    "sla-time-by-owner": SlaTimeByOwner,
    "sla-time-by-phase": SlaTimeByPhase,
    "sla-top-reasons": SlaTopReasons,
    "sla-distribution-table": SlaDistributionTable,
    "sla-empty-state": SlaEmptyState,
  },

  data() {
    return {
      // 'loading' | 'ready' | 'vacio' | 'error'
      uiState: "loading",
      data: null,
      requestKey: null,
      consultHandler: null,
      // Respaldo por si la API no responde: la fecha la manda el backend, que
      // es donde vive la constante, pero el estado vacío tiene que poder
      // escribirse igual.
      medicionDesdeFallback: "2026-10-19",
    };
  },

  computed: {
    medicionDesde() {
      return (this.data && this.data.medicion_desde) || this.medicionDesdeFallback;
    },

    // Las cinco gráficas comparten una sola llamada, así que basta un criterio
    // para toda la pestaña: si no hay procesos medidos, no hay nada que pintar.
    hayDatos() {
      return !!(this.data && this.data.procesos && this.data.procesos.total > 0);
    },

    // Dos estados vacíos, no uno. "No hay datos en este rango" es accionable:
    // el cliente prueba otro rango. "La medición todavía no empieza" no lo es:
    // solo espera. Decirle lo primero cuando pasa lo segundo lo manda a
    // buscar algo que no existe.
    //
    // Se mira el inicio del rango y no el final: si el rango arranca antes de
    // que hubiera medición, lo que explica el vacío es la fecha de arranque,
    // no el rango, y cambiar el rango no le va a traer nada.
    motivoVacio() {
      if (!this.startDate) return "sin_datos";
      return this.startDate < this.medicionDesde ? "sin_medicion" : "sin_datos";
    },
  },

  methods: {
    getUrl() {
      const base = this.url().replace(/\/+$/, "");
      let url = base + "/api/v1/sla/compliance";
      if (this.startDate && this.endDate) {
        url += "?start_date=" + this.startDate + "&end_date=" + this.endDate;
      }
      return url;
    },

    async initializeData() {
      const key = (this.startDate || "") + "|" + (this.endDate || "");
      this.requestKey = key;
      this.uiState = "loading";

      try {
        const request = await this.requestGetWithoutContent(this.getUrl(), {});

        if (this.requestKey !== key) return;

        if (!request || request.status !== 200 || !request.data) {
          this.uiState = "error";
          return;
        }

        this.data = request.data;
        this.uiState = this.hayDatos ? "ready" : "vacio";
      } catch (e) {
        if (this.requestKey !== key) return;
        console.warn("[sla] no se pudo cargar el cumplimiento:", e);
        this.uiState = "error";
      }
    },
  },

  mounted() {
    this.initializeData();
    this.consultHandler = () => this.initializeData();
    eventBus.$on("consultData", this.consultHandler);
  },

  beforeDestroy() {
    if (this.consultHandler) eventBus.$off("consultData", this.consultHandler);
  },
};
</script>

<style scoped>
.gw-sla-centro {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 70px 16px;
  text-align: center;
}
.gw-sla-centro-titulo {
  font-weight: 600;
  font-size: 14px;
  color: #4b4b4b;
}
.gw-sla-centro-texto {
  font-size: 12px;
  color: #9a9a9a;
}
.gw-spinner {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: 3px solid rgba(95, 0, 145, 0.18);
  border-top-color: rgba(95, 0, 145, 0.9);
  animation: gwspin 0.9s linear infinite;
}
@keyframes gwspin {
  to {
    transform: rotate(360deg);
  }
}
.gw-advertencia {
  font-size: 12px;
  color: #8a6d1f;
  background: #fdf7e3;
  border: 1px solid #f0e2b6;
  border-radius: 8px;
  padding: 10px 14px;
  margin-bottom: 18px;
}
</style>
